import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import SEO from '../shared/SEO';
import StatusBadge from '../shared/StatusBadge';
import ProjectsTab from './ProjectsTab';
import MessagesTab from './MessagesTab';
import DocumentsTab from './DocumentsTab';
import { LogIn, LogOut, DollarSign, Clock, FileText, CreditCard, CheckCircle, MessageSquare, Settings, Activity } from 'lucide-react';
import api from '../../services/api';


export default function ClientPortal() {
  const { setCurrentView, portalUser, clientLogin, logout, invoices, fetchInvoices, loading, error, clientChangePassword, notificationSummary, fetchNotificationSummary } = useApp();
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [localError, setLocalError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [projects, setProjects] = useState([]);

  // Change password state (hoisted to top to preserve hook order)
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwdMessage, setPwdMessage] = useState('');

  // Invoice documents modal state (moved to top to keep hooks stable across auth toggles)
  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [invoiceSort, setInvoiceSort] = useState('newest');
  const [invoicePage, setInvoicePage] = useState(1);
  const invoicePageSize = 5;
  const [dashboardDocs, setDashboardDocs] = useState([]);
  const [dashboardMessages, setDashboardMessages] = useState([]);
  const [preferredMessageProjectId, setPreferredMessageProjectId] = useState('');
  const [dashboardLoading, setDashboardLoading] = useState(false);

  useEffect(() => { if (portalUser) fetchInvoices(); }, [portalUser, fetchInvoices]);

  useEffect(() => {
    if (portalUser) {
      fetchProjects();
    }
  }, [portalUser]);

  const docsSeenKey = `clientDocsSeenAt:${portalUser?.id || portalUser?._id || portalUser?.email || 'unknown'}`;

  useEffect(() => {
    if (!portalUser) return;
    const documentsSince = localStorage.getItem(docsSeenKey) || undefined;
    fetchNotificationSummary({ documentsSince });
    const intervalId = setInterval(() => {
      const nextSince = localStorage.getItem(docsSeenKey) || undefined;
      fetchNotificationSummary({ documentsSince: nextSince });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [portalUser, docsSeenKey, fetchNotificationSummary]);

  useEffect(() => {
    if (!portalUser) return;
    if (activeTab === 'documents') {
      const nowIso = new Date().toISOString();
      localStorage.setItem(docsSeenKey, nowIso);
      fetchNotificationSummary({ documentsSince: nowIso });
      return;
    }
    const documentsSince = localStorage.getItem(docsSeenKey) || undefined;
    fetchNotificationSummary({ documentsSince });
  }, [activeTab, portalUser, docsSeenKey, fetchNotificationSummary]);

  useEffect(() => {
    if (!portalUser) return;
    if (activeTab !== 'dashboard') return;
    loadDashboardData();
  }, [portalUser, activeTab, projects.length]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('clientToken');
      const clientId = localStorage.getItem('clientId');
      if (token && clientId) {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const res = await fetch(`${API_URL}/clients/${clientId}/projects`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setProjects(data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const loadDashboardData = async () => {
    if (!portalUser) return;
    try {
      setDashboardLoading(true);
      const clientId = localStorage.getItem('clientId') || portalUser?.id || portalUser?._id;
      const docsPromise = clientId ? api.get(`/clients/${clientId}/documents`) : Promise.resolve({ data: [] });
      const messagePromises = (projects || []).map((p) =>
        api.get(`/messages/project/${p._id}`)
          .then((res) => ({ projectId: p._id, projectName: p.name, messages: res.data || [] }))
          .catch(() => ({ projectId: p._id, projectName: p.name, messages: [] }))
      );
      const [docsRes, projectMessages] = await Promise.all([docsPromise, Promise.all(messagePromises)]);

      const docs = docsRes?.data || [];
      setDashboardDocs(docs);

      const flattened = projectMessages.flatMap((pm) =>
        (pm.messages || []).map((m) => ({
          ...m,
          _projectId: pm.projectId,
          _projectName: pm.projectName
        }))
      );
      setDashboardMessages(flattened);

      const latestUnread = [...flattened]
        .filter((m) => !m.isRead && m.sender !== 'client')
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
      setPreferredMessageProjectId(latestUnread?._projectId || (projects[0]?._id || ''));
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setDashboardDocs([]);
      setDashboardMessages([]);
    } finally {
      setDashboardLoading(false);
    }
  };

  // Read invite token and optional email from URL query params (e.g., /portal?invite=TOKEN&email=you@example.com)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('invite') || params.get('token');
      const email = params.get('email');
      const invoiceId = params.get('invoiceId');
      const paymentStatus = params.get('payment');
      if (token) setInviteToken(token);
      if (email) setLoginEmail(email);
      if (paymentStatus === 'success') {
        // Refresh invoices to pick up the new payment status and briefly show message
        fetchInvoices();
        setLocalError('Payment successful — thank you!');
        setTimeout(() => setLocalError(''), 7000);
      } else if (paymentStatus === 'cancel') {
        setLocalError('Payment cancelled.');
        setTimeout(() => setLocalError(''), 7000);
      }

      // Remove payment-related query params (so the message doesn't persist when user navigates)
      params.delete('payment');
      params.delete('invoiceId');
      const newQuery = params.toString();
      const newUrl = newQuery ? `${window.location.pathname}?${newQuery}` : window.location.pathname;
      try { window.history.replaceState({}, '', newUrl); } catch (e) { /* ignore */ }
    } catch (e) {
      // ignore
    }

    // Handle navigation via browser Back/Forward — clear payment params and messages when user returns from Checkout
    const handlePop = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('payment') || params.get('invoiceId')) {
          // Clear messages and refresh invoice state
          setLocalError('');
          fetchInvoices();
          params.delete('payment');
          params.delete('invoiceId');
          const q = params.toString();
          const url = q ? `${window.location.pathname}?${q}` : window.location.pathname;
          try { window.history.replaceState({}, '', url); } catch (e) { }
        }
      } catch (err) { /* ignore */ }
    };

    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    try {
      await clientLogin({ email: loginEmail, password: loginPassword || undefined, inviteToken: inviteToken || undefined });
    } catch (err) {
      setLocalError(err.response?.data?.error || 'Login failed');
    }
  };

  const handleCreateCheckout = async (invoice, amount, paymentMethod) => {
    try {
      setLocalError('');
      const body = {};
      if (typeof amount !== 'undefined') body.amount = amount; // dollars
      if (paymentMethod) body.paymentMethod = paymentMethod;
      // If user is authenticated (portal) the API will use token; otherwise backend requires clientEmail to match invoice.clientEmail
      const res = await api.post(`/invoices/${invoice._id}/checkout`, body);
      if (res && res.data && res.data.url) {
        window.location.href = res.data.url;
      } else if (res && res.data && res.data.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      } else {
        setLocalError('Failed to create checkout session');
      }
    } catch (err) {
      setLocalError(err.response?.data?.error || 'Failed to create checkout session');
    }
  };

  const inputStyle = { width: '100%', padding: '0.75rem 1rem', borderRadius: '0.75rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', outline: 'none' };
  const btnPrimary = { padding: '0.875rem 1.5rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#020617', fontWeight: 600, borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%' };
  const cardStyle = { background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem' };

  if (!portalUser) {
    return (
      <>
        <SEO title="Client Portal | PageHyve Studios" description="Access your invoices" noIndex={true} />
        <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #020617, #0f172a)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
          <div style={{ width: '100%', maxWidth: '28rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{ width: '4rem', height: '4rem', margin: '0 auto 1rem', borderRadius: '1rem', background: 'linear-gradient(to br, #34d399, #22d3ee)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="/logos/pagehyve-icon-color.svg" alt="PageHyve" style={{ width: '2rem', height: '2rem' }} />
              </div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Client Portal</h1>
              <p style={{ color: '#94a3b8' }}>Sign in to view your invoices</p>
            </div>
            <form onSubmit={handleLogin} style={{ background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem', padding: '2rem' }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label htmlFor="portal-email" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Email Address</label>
                <input id="portal-email" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} style={inputStyle} placeholder="your@email.com" required autoComplete="email" />
              </div>

              <div style={{ marginBottom: '1.25rem' }}>
                <label htmlFor="portal-password" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                  {inviteToken ? 'Create a Password *' : 'Password (optional)'}
                </label>
                <input id="portal-password" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} style={inputStyle} placeholder={inviteToken ? 'Create a new password' : 'Enter your password'} required={!!inviteToken} autoComplete={inviteToken ? 'new-password' : 'current-password'} />
              </div>

              {inviteToken && <div style={{ marginBottom: '1rem', fontSize: '0.75rem', color: '#64748b' }}>✓ Using invite token from link • Create any password you want</div>}

              {(localError || error) && <div style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '0.5rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', fontSize: '0.875rem' }}>{localError || error}</div>}
              <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.5 : 1 }}><LogIn style={{ width: '1rem', height: '1rem' }} />{loading ? 'Signing in...' : 'Sign In'}</button>
              {/*<p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#64748b', marginTop: '1rem' }}>Demo: contact@techstart.io</p>*/}
      
            </form>
            <button onClick={() => setCurrentView('public')} style={{ width: '100%', marginTop: '1rem', fontSize: '0.875rem', color: '#64748b' }}>← Back to Website</button>
          </div>
        </div>
      </>
    );
  }

  const clientInvoices = invoices.filter(inv => inv.clientEmail === portalUser.email);
  const sortedClientInvoices = [...clientInvoices].sort((a, b) => {
    if (invoiceSort === 'oldest') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    if (invoiceSort === 'due_soon') return new Date(a.dueDate || 0) - new Date(b.dueDate || 0);
    if (invoiceSort === 'amount_high') return Number(b.total || 0) - Number(a.total || 0);
    if (invoiceSort === 'amount_low') return Number(a.total || 0) - Number(b.total || 0);
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
  const invoiceTotalPages = Math.max(1, Math.ceil(sortedClientInvoices.length / invoicePageSize));
  const pagedClientInvoices = sortedClientInvoices.slice((invoicePage - 1) * invoicePageSize, invoicePage * invoicePageSize);
  const totalPaid = clientInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0);
  const totalPending = clientInvoices.filter(inv => ['sent', 'overdue'].includes(inv.status)).reduce((sum, inv) => sum + inv.total, 0);
  const overdueInvoicesCount = clientInvoices.filter((inv) => inv.status === 'overdue').length;
  const unreadMessagesCount = Number(notificationSummary?.messagesUnread || 0);
  const now = new Date();
  const fourteenDays = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
  const outstandingBalance = clientInvoices
    .filter((inv) => !['paid', 'cancelled', 'draft'].includes(inv.status))
    .reduce((sum, inv) => sum + Math.max(0, Number(inv.total || 0) - (Number(inv.paidAmount || 0) / 100)), 0);
  const upcomingDue14 = clientInvoices
    .filter((inv) => ['sent', 'overdue', 'partial'].includes(inv.status))
    .filter((inv) => {
      const due = new Date(inv.dueDate || 0);
      return due >= now && due <= fourteenDays;
    })
    .reduce((sum, inv) => sum + Math.max(0, Number(inv.total || 0) - (Number(inv.paidAmount || 0) / 100)), 0);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const paidThisMonth = clientInvoices
    .filter((inv) => inv.paidAt && new Date(inv.paidAt) >= startOfMonth)
    .reduce((sum, inv) => sum + Number(inv.total || 0), 0);
  const lastPaymentInvoice = [...clientInvoices]
    .filter((inv) => inv.paidAt)
    .sort((a, b) => new Date(b.paidAt || 0) - new Date(a.paidAt || 0))[0];
  const nextProjectDeadline = [...projects]
    .filter((p) => p.dueDate && new Date(p.dueDate) >= now)
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0];
  const nextDueInvoice = [...clientInvoices]
    .filter((inv) => ['sent', 'overdue', 'partial'].includes(inv.status))
    .filter((inv) => Math.max(0, Number(inv.total || 0) - (Number(inv.paidAmount || 0) / 100)) > 0)
    .sort((a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0))[0];

  const recentActivity = (() => {
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const attachmentDirection = new Map(
      (dashboardMessages || [])
        .filter((m) => m.attachment && m.attachment._id)
        .map((m) => [String(m.attachment._id), m.sender === 'client' ? 'sent' : 'received'])
    );
    const docEvents = (dashboardDocs || [])
      .filter((d) => new Date(d.createdAt || 0) >= weekAgo)
      .map((d) => {
        const direction = attachmentDirection.get(String(d._id || ''));
        const verb = direction === 'sent' ? 'sent' : direction === 'received' ? 'received' : 'added';
        return {
          type: 'document',
          text: `Document ${verb}: ${d.originalName}`,
          at: d.createdAt,
        };
      });
    const msgEvents = (dashboardMessages || [])
      .filter((m) => new Date(m.createdAt || 0) >= weekAgo)
      .map((m) => ({
        type: 'message',
        text: `Message ${m.sender === 'client' ? 'sent' : 'received'} (${m._projectName || 'Project'}): ${String(m.content || '').slice(0, 70)}${String(m.content || '').length > 70 ? '...' : ''}`,
        at: m.createdAt,
      }));
    const invoiceEvents = clientInvoices.flatMap((inv) => {
      const out = [];
      if (inv.sentAt && new Date(inv.sentAt) >= weekAgo) out.push({ type: 'invoice', text: `${inv.invoiceNumber} received`, at: inv.sentAt });
      if (inv.paidAt && new Date(inv.paidAt) >= weekAgo) out.push({ type: 'payment', text: `${inv.invoiceNumber} paid`, at: inv.paidAt });
      return out;
    });
    return [...docEvents, ...msgEvents, ...invoiceEvents]
      .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0))
      .slice(0, 8);
  })();

  useEffect(() => {
    setInvoicePage(1);
  }, [invoiceSort, clientInvoices.length]);

  const fetchInvoiceDocs = async (invoiceId) => {
    try {
      setDocsLoading(true);
      const api = await import('../../services/api');
      const res = await api.getInvoiceDocuments(invoiceId);
      setInvoiceDocs(res || []);
      setShowDocsModal(true);
    } catch (err) {
      console.error('Failed to fetch invoice documents', err);
      setInvoiceDocs([]);
    } finally { setDocsLoading(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdMessage('');
    try {
      await clientChangePassword(currentPassword || undefined, newPassword);
      setPwdMessage('Password updated successfully');
      setCurrentPassword(''); setNewPassword('');
      setTimeout(() => setPwdMessage(''), 4000);
    } catch (err) {
      setPwdMessage(err.response?.data?.error || 'Failed to update password');
      setTimeout(() => setPwdMessage(''), 4000);
    }
  };

  return (
    <>
      <SEO title="Client Portal | PageHyve Studios" noIndex={true} />
      <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #020617, #0f172a)' }}>
        <header style={{ position: 'sticky', top: 0, zIndex: 50, backdropFilter: 'blur(24px)', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(2,6,23,0.5)' }}>
          <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="portal-brand" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div className="portal-brand-mark" style={{ borderRadius: '0.75rem', background: 'linear-gradient(to br, #34d399, #22d3ee)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img className="portal-brand-logo" src="/logos/pagehyve-icon-color.svg" alt="PageHyve" />
              </div>
              <span className="portal-brand-title" style={{ fontWeight: 700 }}>Client Portal</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span className="header-email" style={{ fontSize: '0.875rem', color: '#94a3b8' }}>{portalUser.email}</span>
              <button onClick={logout} style={{ padding: '0.5rem', color: '#94a3b8' }}><LogOut style={{ width: '1.25rem', height: '1.25rem' }} /></button>
            </div>
          </div>
        </header>

        <main style={{ maxWidth: '72rem', margin: '0 auto', padding: '2rem 1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '2rem' }}>Welcome, {portalUser.name}!</h1>

          {/* Tabs */}
          <div className="portal-tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '1rem' }}>
            {[
              { id: 'dashboard', label: 'Dashboard', icon: Activity },
              { id: 'invoices', label: 'Invoices', icon: FileText },
              { id: 'projects', label: 'Projects', icon: CheckCircle, count: notificationSummary?.reviewRequestsUnread || 0 },
              { id: 'documents', label: 'Documents', icon: FileText, count: notificationSummary?.documentsNew || 0 },
              { id: 'messages', label: 'Messages', icon: MessageSquare, count: notificationSummary?.messagesUnread || 0 },
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    background: activeTab === tab.id ? 'rgba(16,185,129,0.2)' : 'transparent',
                    border: activeTab === tab.id ? '1px solid rgba(16,185,129,0.5)' : '1px solid transparent',
                    borderRadius: '0.75rem',
                    color: activeTab === tab.id ? '#34d399' : '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: activeTab === tab.id ? '600' : '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Icon size={18} />
                  {tab.label}
                  {Number(tab.count) > 0 && (
                    <span style={{ minWidth: '1.2rem', height: '1.2rem', borderRadius: '999px', background: '#ef4444', color: '#fff', fontSize: '0.7rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 0.3rem' }}>
                      {tab.count > 99 ? '99+' : tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          {activeTab === 'dashboard' && (
            <>
              <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: '1rem' }}>
                <div style={{ ...cardStyle, padding: '1rem' }}>
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.35rem' }}>Action Required</div>
                  <div style={{ display: 'grid', gap: '0.4rem', fontSize: '0.9rem' }}>
                    <div>Unread messages: <strong>{unreadMessagesCount}</strong></div>
                    <div>Overdue invoices: <strong>{overdueInvoicesCount}</strong></div>
                    <div>Next project deadline: <strong>{nextProjectDeadline ? new Date(nextProjectDeadline.dueDate).toLocaleDateString() : 'None'}</strong></div>
                  </div>
                </div>
                <div style={{ ...cardStyle, padding: '1rem' }}>
                  <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.35rem' }}>Financial Snapshot</div>
                  <div style={{ display: 'grid', gap: '0.4rem', fontSize: '0.9rem' }}>
                    <div>Outstanding: <strong>${outstandingBalance.toLocaleString()}</strong></div>
                    <div>Paid this month: <strong>${paidThisMonth.toLocaleString()}</strong></div>
                    <div>Due in 14 days: <strong>${upcomingDue14.toLocaleString()}</strong></div>
                    <div>Last payment: <strong>{lastPaymentInvoice ? `${lastPaymentInvoice.invoiceNumber} • $${Number(lastPaymentInvoice.total || 0).toLocaleString()}` : 'N/A'}</strong></div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                <div style={{ ...cardStyle, padding: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>This Week Timeline</h2>
                    {dashboardLoading && <span style={{ color: '#64748b', fontSize: '0.8rem' }}>Refreshing...</span>}
                  </div>
                  {recentActivity.length === 0 ? (
                    <div style={{ color: '#64748b', fontSize: '0.9rem' }}>No recent activity in the past 7 days.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: '0.55rem' }}>
                      {recentActivity.map((item, idx) => (
                        <div key={`${item.type}-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '0.45rem' }}>
                          <span style={{ fontSize: '0.9rem', color: '#e2e8f0' }}>{item.text}</span>
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>{new Date(item.at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ ...cardStyle, padding: '1rem' }}>
                  <h2 style={{ margin: 0, marginBottom: '0.75rem', fontSize: '1rem', fontWeight: 600 }}>Quick Actions</h2>
                  <div style={{ display: 'grid', gap: '0.5rem' }}>
                    <button onClick={() => {
                      if (Number(notificationSummary?.reviewRequestsUnread || 0) > 0) setActiveTab('projects');
                      else setActiveTab('messages');
                    }} style={{ textAlign: 'left', padding: '0.55rem 0.7rem', borderRadius: '0.55rem', border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1' }}>
                      {Number(notificationSummary?.reviewRequestsUnread || 0) > 0 ? 'Open pending review request' : 'Open latest unread message'}
                    </button>
                    <button onClick={() => {
                      setActiveTab('invoices');
                      if (nextDueInvoice?._id) {
                        setInvoiceSort('due_soon');
                        setInvoicePage(1);
                        setExpandedInvoices((prev) => ({ ...prev, [nextDueInvoice._id]: true }));
                      }
                    }} style={{ textAlign: 'left', padding: '0.55rem 0.7rem', borderRadius: '0.55rem', border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1' }}>
                      Pay next due invoice
                    </button>
                    <button onClick={() => setActiveTab('documents')} style={{ textAlign: 'left', padding: '0.55rem 0.7rem', borderRadius: '0.55rem', border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1' }}>
                      View newest document
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'invoices' && (
            <>
              <div className="client-stats">
                {[[DollarSign, 'Total Paid', `$${totalPaid.toLocaleString()}`, '#34d399'], [Clock, 'Pending', `$${totalPending.toLocaleString()}`, '#fbbf24'], [FileText, 'Invoices', clientInvoices.length, '#22d3ee']].map(([Icon, label, value, color], i) => (
                  <div key={i} className="client-stat-card">
                    <div className="client-stat-head"><Icon style={{ width: '1.25rem', height: '1.25rem', color }} /><span className="client-stat-label">{label}</span></div>
                    <p className="client-stat-value" style={{ color }}>{value}</p>
                  </div>
            ))}
          </div>

          <div style={{ background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem', overflow: 'hidden' }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <h2 style={{ fontWeight: 600 }}>Your Invoices</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Sort</span>
                  <select value={invoiceSort} onChange={(e) => setInvoiceSort(e.target.value)} style={{ padding: '0.45rem 0.6rem', borderRadius: '0.5rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="due_soon">Due Soon</option>
                    <option value="amount_high">Amount High-Low</option>
                    <option value="amount_low">Amount Low-High</option>
                  </select>
                </div>
              </div>
              {localError && <div style={{ marginTop: '0.5rem', color: '#f87171' }}>{localError}</div>}
            </div>
            {clientInvoices.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>No invoices found</div>
            ) : (
              pagedClientInvoices.map((invoice, index) => {
                const isExpanded = !!expandedInvoices[invoice._id];
                return (
                <div key={invoice._id} style={{ padding: '1.5rem', borderBottom: index < pagedClientInvoices.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: 600 }}>{invoice.invoiceNumber}</span>
                        <StatusBadge status={invoice.status} />
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>Due: {new Date(invoice.dueDate).toLocaleDateString()}</p>
                    </div>
                    <div className="invoice-heading" style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%' }}>
                      <span className="invoice-amount" style={{ fontSize: '1.25rem', fontWeight: 700 }}>${(invoice.payable?.chargedAmount || invoice.total).toLocaleString()}</span>
                      {['sent', 'overdue'].includes(invoice.status) && (
                        <div className="pay-actions" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <button className="pay-btn" onClick={() => handleCreateCheckout(invoice)}>
                            <CreditCard style={{ width: '1rem', height: '1rem' }} /> Pay Full
                          </button>
                          <button className="pay-btn" onClick={() => {
                            const paidCents = invoice.paidAmount || 0;
                            const remaining = (invoice.total * 100 - paidCents) / 100;
                            const half = Math.max(1, Number((remaining * 0.5).toFixed(2)));
                            handleCreateCheckout(invoice, half);
                          }}>
                            <CreditCard style={{ width: '1rem', height: '1rem' }} /> Pay 50%
                          </button>
                        </div>
                      )}
                      <button className="docs-btn" onClick={() => fetchInvoiceDocs(invoice._id)} style={{ padding: '0.5rem', background: 'transparent', color: '#94a3b8', borderRadius: '0.5rem', marginLeft: 'auto' }}>Documents</button>
                      <button
                        onClick={() => setExpandedInvoices((prev) => ({ ...prev, [invoice._id]: !prev[invoice._id] }))}
                        style={{ padding: '0.5rem 0.75rem', borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1', background: 'rgba(15,23,42,0.35)' }}
                      >
                        {isExpanded ? 'Hide Details' : 'View Details'}
                      </button>
                    </div>
                  </div>
                  {isExpanded && (
                  <div className="invoice-list">
                    <div className="invoice-items responsive-table" style={{ background: 'rgba(15,23,42,0.3)', borderRadius: '0.75rem', padding: '1rem' }}>
                      <table style={{ width: '100%', fontSize: '0.875rem' }}>
                        <thead><tr style={{ color: '#64748b' }}><th style={{ textAlign: 'left', paddingBottom: '0.5rem' }}>Description</th><th style={{ textAlign: 'right', paddingBottom: '0.5rem' }}>Qty</th><th style={{ textAlign: 'right', paddingBottom: '0.5rem' }}>Rate</th><th style={{ textAlign: 'right', paddingBottom: '0.5rem' }}>Amount</th></tr></thead>
                        <tbody style={{ color: '#cbd5e1' }}>
                          {invoice.items.map((item, i) => (
                            <tr key={i}>
                              <td data-label="Description" style={{ padding: '0.25rem 0' }}>{item.name}</td>
                              <td data-label="Qty" style={{ textAlign: 'right' }}>{item.quantity}</td>
                              <td data-label="Rate" style={{ textAlign: 'right' }}>${item.rate.toLocaleString()}</td>
                              <td data-label="Amount" style={{ textAlign: 'right' }}>${(item.quantity * item.rate).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                          <tr><td colSpan="3" style={{ textAlign: 'right', paddingTop: '0.5rem', color: '#64748b' }}>Subtotal:</td><td style={{ textAlign: 'right', paddingTop: '0.5rem' }}>${invoice.subtotal.toLocaleString()}</td></tr>
                          {invoice.discount > 0 && <tr><td colSpan="3" style={{ textAlign: 'right', color: '#64748b' }}>Discount:</td><td style={{ textAlign: 'right', color: '#34d399' }}>-${invoice.discount.toLocaleString()}</td></tr>}
                          <tr><td colSpan="3" style={{ textAlign: 'right', color: '#64748b' }}>Tax ({invoice.taxRate}%):</td><td style={{ textAlign: 'right' }}>${invoice.tax.toLocaleString()}</td></tr>
                          <tr style={{ fontWeight: 600, fontSize: '1rem' }}><td colSpan="3" style={{ textAlign: 'right', paddingTop: '0.5rem' }}>Total:</td><td style={{ textAlign: 'right', paddingTop: '0.5rem' }}>${(invoice.total).toLocaleString()}</td></tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                  )}
                </div>
              )})
            )}
            {clientInvoices.length > invoicePageSize && (
              <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Page {invoicePage} of {invoiceTotalPages}</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => setInvoicePage((p) => Math.max(1, p - 1))} disabled={invoicePage <= 1} style={{ padding: '0.4rem 0.75rem', borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', opacity: invoicePage <= 1 ? 0.5 : 1 }}>Prev</button>
                  <button onClick={() => setInvoicePage((p) => Math.min(invoiceTotalPages, p + 1))} disabled={invoicePage >= invoiceTotalPages} style={{ padding: '0.4rem 0.75rem', borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', opacity: invoicePage >= invoiceTotalPages ? 0.5 : 1 }}>Next</button>
                </div>
              </div>
            )}
          </div>



          {/* Invoice documents modal */}
          {showDocsModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
              <div style={{ width: '100%', maxWidth: '40rem', background: '#0f172a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '1rem', padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0 }}>Invoice Documents</h3>
                  <button onClick={() => setShowDocsModal(false)} style={{ color: '#94a3b8' }}>Close</button>
                </div>
                <div style={{ marginTop: '1rem' }}>
                  {docsLoading ? <div style={{ color: '#64748b' }}>Loading...</div> : (
                    invoiceDocs.length === 0 ? <div style={{ color: '#64748b' }}>No documents attached</div> : (
                      <div style={{ display: 'grid', gap: '0.5rem' }}>
                        {invoiceDocs.map(d => (
                          <div key={d._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', background: 'rgba(15,23,42,0.3)', borderRadius: '0.375rem' }}>
                            <a href={d.signedUrl || d.url} target="_blank" rel="noreferrer" style={{ color: '#34d399' }}>{d.originalName}</a>
                            <span style={{ fontSize: '0.875rem', color: '#94a3b8' }}>{(d.size / 1024).toFixed(1)} KB</span>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
            </>
          )}

          {activeTab === 'projects' && <ProjectsTab portalUser={portalUser} />}

          {activeTab === 'documents' && <DocumentsTab portalUser={portalUser} />}

          {activeTab === 'messages' && (
            <MessagesTab
              portalUser={portalUser}
              projects={projects}
              initialProjectId={preferredMessageProjectId}
              onMessagesRead={() => {
                const documentsSince = localStorage.getItem(docsSeenKey) || undefined;
                fetchNotificationSummary({ documentsSince });
              }}
            />
          )}

          {activeTab === 'settings' && (
            <div style={{ marginTop: '1.5rem', maxWidth: '36rem' }}>
              <form onSubmit={handleChangePassword} style={{ ...cardStyle, padding: '1rem', display: 'grid', gap: '1rem' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Change Password</h3>
                <div>
                  <label htmlFor="current-password" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Current Password</label>
                  <input id="current-password" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={inputStyle} placeholder="Current password (leave blank if none)" />
                </div>
                <div>
                  <label htmlFor="new-password" style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>New Password</label>
                  <input id="new-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} placeholder="At least 6 characters" required />
                </div>
                {pwdMessage && <div style={{ fontSize: '0.875rem', color: pwdMessage.includes('successfully') ? '#34d399' : '#f87171' }}>{pwdMessage}</div>}
                <button type="submit" style={{ ...btnPrimary, width: 'auto' }}>Update Password</button>
              </form>
            </div>
          )}

          <button onClick={() => setCurrentView('public')} style={{ marginTop: '2rem', fontSize: '0.875rem', color: '#64748b' }}>← Back to Website</button>
        </main>
      </div>
    </>
  );
}
